import { LogManager, Navigation } from "@mcleod/core";
import { Dialog, Overlay, ScreenStack, SlideoutDecorator } from "@mcleod/components";
import { Router } from "@mcleod/common";
import { CapacitorAccess } from "../capacitor";
import { AppBackButtonEvent, AppPluginDef } from "../app";

const log = LogManager.getLogger("common.AppBackButtonHandler");

/**
 * Return true when the interceptor consumed the back press; false to let the default chain continue.
 */
export type BackInterceptor = () => boolean | Promise<boolean>;

/**
 * Owns the Android hardware / gesture back button so that it behaves the way users expect from a native app.
 *
 * Without this, Capacitor's default is to walk the WebView's browser history and then exit the process, which
 * from inside a McLeod single-page layout means "back" either does nothing useful or kills the app.
 *
 * Resolution order on each press:
 *   1. Interceptors registered by the current screen (most recently registered first).  A screen that has its
 *      own idea of "back" (cancel a download, collapse an expanded panel) registers one while it is mounted.
 *   2. The newest open overlay: slideouts are asked to close through their own onClose so they animate out and
 *      run their cleanup; dialogs are canceled; anything else is simply hidden.
 *   3. McLeod route history, when the router is showing something other than the home route.
 *   4. Otherwise, the app is minimized (never exited) so the user lands back on the launcher with the app warm.
 *
 * iOS has no back button; initialize() is a no-op there and on the web.
 */
export class AppBackButtonHandler {
    private static _instance: AppBackButtonHandler | null = null;
    private readonly interceptors: BackInterceptor[] = [];
    private handling = false;

    private constructor(private readonly appPlugin: AppPluginDef) {}

    /**
     * Registers the native listener once.  Safe to call repeatedly; only the first call has an effect.
     */
    public static initialize(): void {
        if (this._instance != null || !CapacitorAccess.isNativePlatform)
            return;
        const appPlugin = CapacitorAccess.plugins?.App;
        if (appPlugin?.addListener == null) {
            log.debug("Capacitor App plugin not available; hardware back button will use platform defaults.");
            return;
        }
        this._instance = new AppBackButtonHandler(appPlugin);
        Promise.resolve(appPlugin.addListener("backButton", (event) => this._instance?.handleBackPress(event)))
            .catch((error) => log.error("Unable to register backButton listener", error));
    }

    /**
     * Adds an interceptor that gets first say on the next back presses.  Returns a function that removes it;
     * screens should call that from an unmount listener.
     */
    public static pushInterceptor(interceptor: BackInterceptor): () => void {
        const instance = this._instance;
        if (instance == null)
            return () => {};
        instance.interceptors.push(interceptor);
        return () => {
            const index = instance.interceptors.lastIndexOf(interceptor);
            if (index >= 0)
                instance.interceptors.splice(index, 1);
        };
    }

    /**
     * Applies the resolution chain described on the class.  Exposed for unit tests via the returned action name.
     */
    public async handleBackPress(event?: AppBackButtonEvent): Promise<"intercepted" | "overlay" | "history" | "minimize" | "ignored"> {
        if (this.handling)
            return "ignored"; // a slideout animation or async interceptor is still running; swallow the repeat press
        this.handling = true;
        try {
            for (let i = this.interceptors.length - 1; i >= 0; i--) {
                if (await this.interceptors[i]() === true)
                    return "intercepted";
            }

            const overlay = ScreenStack.getNewestOverlay();
            if (overlay != null) {
                AppBackButtonHandler.closeOverlay(overlay);
                return "overlay";
            }

            if (AppBackButtonHandler.canNavigateBack(event)) {
                await Navigation.navigateBack();
                return "history";
            }

            await this.appPlugin.minimizeApp();
            return "minimize";
        } catch (error) {
            log.error("Error handling back press", error);
            return "ignored";
        } finally {
            this.handling = false;
        }
    }

    private static closeOverlay(overlay: Overlay): void {
        const component = overlay.component;
        if (component instanceof SlideoutDecorator && component.onClose != null)
            component.onClose(true);
        else if (component instanceof Dialog)
            component.close(undefined);
        else
            Overlay.hideOverlay(overlay);
    }

    /**
     * McLeod pages are pushed onto browser history by Navigation, so the WebView's own canGoBack flag is a
     * reliable signal that there is a McLeod route to return to.  The home route is treated as the bottom of
     * the stack even when history says otherwise (e.g. right after an OTA bundle reload).
     */
    private static canNavigateBack(event?: AppBackButtonEvent): boolean {
        if (event?.canGoBack !== true)
            return false;
        const router = Router.instance;
        if (router?._route == null)
            return false;
        return !Router.isDisplayingHomePage();
    }
}
